// Central SEO helper for TanStack Start route `head()` functions.
//
// IMPORTANT: per-page <title>, description, canonical and JSON-LD must be
// declared here (via each route's `head()`), NOT with react-helmet-async.
// Helmet does not render into the SSR stream in TanStack Start, so anything
// declared with it is invisible to crawlers and link-preview bots.

import type { AnyRouteMatch } from "@tanstack/react-router";

/** Canonical origin for the site. Change in ONE place if the host changes. */
export const SITE_ORIGIN = "https://cedarcreekconstruct.com";
export const SITE_NAME = "Cedar Creek Construction";
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.png`;

/** Absolute URL for a site-relative path, with trailing slash normalised. */
export const absoluteUrl = (path: string) => {
  if (/^https?:\/\//.test(path)) return path;
  let p = path.startsWith("/") ? path : `/${path}`;
  if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
  return `${SITE_ORIGIN}${p}`;
};

export interface SeoOptions {
  /** Page title. " | Cedar Creek Construction" is appended when missing. */
  title: string;
  description: string;
  /** Site-relative path for the canonical URL, e.g. "/services/roof-repair". */
  path: string;
  /** Absolute or site-relative OG image URL. */
  image?: string | undefined;
  /** Open Graph type. Use "article" for blog posts. */
  type?: "website" | "article" | undefined;
  /** One or more schema.org objects to emit as JSON-LD. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[] | undefined;
  /** Keep the page out of the index (admin, thank-you pages, etc.). */
  noindex?: boolean | undefined;
}

type HeadResult = {
  meta: NonNullable<AnyRouteMatch["meta"]>;
  links: Array<Record<string, string>>;
  scripts: Array<Record<string, string>>;
};

/**
 * Build the object a route's `head()` should return.
 *
 *   export const Route = createFileRoute("/_site/roofing")({
 *     head: () => seo({ title: "...", description: "...", path: "/roofing" }),
 *     component: Roofing,
 *   });
 */
export function seo({
  title,
  description,
  path,
  image,
  type = "website",
  jsonLd,
  noindex = false,
}: SeoOptions): HeadResult {
  const fullTitle = title.includes(SITE_NAME)
    ? title
    : `${title} | ${SITE_NAME}`;
  const url = absoluteUrl(path);
  const ogImage = image ? absoluteUrl(image) : DEFAULT_OG_IMAGE;

  const meta: HeadResult["meta"] = [
    { title: fullTitle },
    { name: "description", content: description },

    { property: "og:type", content: type },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:title", content: fullTitle },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { property: "og:image", content: ogImage },

    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: fullTitle },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: ogImage },
  ];

  if (noindex) meta.push({ name: "robots", content: "noindex, nofollow" });

  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return {
    meta,
    links: [{ rel: "canonical", href: url }],
    scripts: schemas.map((data) => ({
      type: "application/ld+json",
      children: JSON.stringify(data),
    })),
  };
}

/** BreadcrumbList schema from [{ name, path }] crumbs. */
export const breadcrumbSchema = (items: { name: string; path: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.path),
  })),
});
